import React, { useState, useEffect, useCallback } from "react";
import {
    Collapse,
    Form,
    Input,
    Select,
    DatePicker,
    Radio,
    Button,
    Row,
    Col,
    message,
    Tooltip,
    Alert,
    InputNumber,
    ConfigProvider,
    Checkbox
} from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";
import "./KaskoIhbarBildirimPage.css";
import { useHasar } from "./HasarContext";
import { useNavigate } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import InputMask from "react-input-mask";
import getConfig from "./config";
import trTR from 'antd/locale/tr_TR';
import dayjs from "dayjs";
import 'dayjs/locale/tr';
import { useApiLogger } from "./hooks/useUIEventLogger";

dayjs.locale('tr');

// Set global message config for position
message.config({
    top: 24,
    right: 24,
    duration: 3,
    maxCount: 3,
});
const { Panel } = Collapse;
const { Option } = Select;

const turkishSearch = (input, option) => {
    const normalize = (str) => {
        return (str || '').toString()
            .toUpperCase()
            .replace(/İ/g, 'I')
            .replace(/Ş/g, 'S')
            .replace(/Ğ/g, 'G')
            .replace(/Ü/g, 'U')
            .replace(/Ö/g, 'O')
            .replace(/Ç/g, 'C');
    };

    const text = (option?.children || option?.label || '').toString();
    return normalize(text).indexOf(normalize(input)) >= 0;
};

const fetchSigortaliBilgisi = async (policeNo, token) => {
    const config = getConfig();
    const response = await fetch(
        `${config.apiBaseUrl}/Hasar/SigortaliBilgiGetir`,
        {
            method: "POST",
            headers:
            {
                "Authorization": `Bearer ${token}`,
                "Content-Type": "application/json"
            },
            body: policeNo
        }
    );
    if (!response.ok) throw new Error("Sigortalı bilgisi alınamadı");
    return await response.json();
};

const fetchTamirhaneBilgisi = async (token, tamirhaneKod) => {
    const config = getConfig();
    const response = await fetch(
        `${config.apiBaseUrl}/Hasar/TamirhaneBilgiGetir`,
        {
            method: "POST",
            headers: {
                "Authorization": `Bearer ${token}`,
                "Content-Type": "application/json"
            },
            body: JSON.stringify({ tamirhaneKod })
        }
    );
    if (!response.ok) throw new Error("Tamirhane bilgisi alınamadı");
    return await response.json();
};

const sendSmsVerification = async (token, gsmNo) => {
    const config = getConfig();
    const response = await fetch(
        `${config.apiBaseUrl}/Hasar/SmsGonder`,
        {
            method: "POST",
            headers: {
                "Authorization": `Bearer ${token}`,
                "Content-Type": "application/json"
            },
            body: JSON.stringify({ gsmNo })
        }
    );
    if (!response.ok) throw new Error("SMS gönderilemedi");
    return await response.json();
};

const verifySmsCode = async (token, gsmNo, dogrulamaKodu) => {
    const config = getConfig();
    const response = await fetch(
        `${config.apiBaseUrl}/Hasar/SmsDogrulamaYap`,
        {
            method: "POST",
            headers: {
                "Authorization": `Bearer ${token}`,
                "Content-Type": "application/json"
            },
            body: JSON.stringify({ gsmNo, dogrulamaKodu })
        }
    );
    if (!response.ok) throw new Error("SMS doğrulaması yapılamadı");
    return await response.json();
};

const submitKaskoIhbar = async (token, data, logApiCall, logApiSuccess, logApiError) => {

    const config = getConfig();
    const endpoint = `${config.apiBaseUrl}/Hasar/HasarDosyaGiris`;
    const callId = logApiCall(endpoint, 'POST', {
        formType: 'KASKO_IHBAR_BILDIRIM'
    });

    const response = await fetch(
        endpoint,
        {
            method: "POST",
            headers: {
                "Authorization": `Bearer ${token}`,
                "Content-Type": "application/json",
                "accept": "*/*",
                timeout: 300000 // 5 minutes in milliseconds
            },
            body: JSON.stringify(data)
        }
    );

    const responseText = await response.text();

    if (!response.ok) {
        // 401 Unauthorized hatası için özel mesaj
        if (response.status === 401) {
            logApiError(callId, endpoint, "401: Unauthorized - Oturum süresi doldu", response.status, {
                success: false,
                request: JSON.stringify(data),
                response: responseText
            });
            throw new Error("401: Unauthorized - Oturum süresi doldu");
        }

        try {
            // JSON parse etmeyi dene
            const errorJson = JSON.parse(responseText);
            const errorMessage = errorJson.error || errorJson.message || responseText;

            logApiError(callId, endpoint, "Girilen bilgiler hatalı", response.status, {
                success: false,
                request: JSON.stringify(data),
                response: errorJson
            });

            throw new Error(errorMessage + "_" + callId);
        } catch (parseError) {
            // JSON değilse direkt text olarak kullan
            if (parseError.message.includes("401")) {
                throw parseError;
            }
            throw new Error(responseText + "_" + callId);
        }
    }

    const responseData = JSON.parse(responseText);

    logApiSuccess(callId, endpoint, {
        success: true,
        request: JSON.stringify(data),
        response: responseData
    });

    return responseData;
};

const fetchIlListesi = async (token) => {
    const config = getConfig();
    const response = await fetch(
        `${config.apiBaseUrl}/Hasar/HasarIlGetir`,
        {
            method: "POST",
            headers: {
                "Authorization": `Bearer ${token}`,
                "Content-Type": "application/json"
            }
        }
    );
    if (!response.ok) throw new Error("İl listesi alınamadı");
    return await response.json();
};

const fetchIlceListesi = async (token, ilKodu, tip) => {
    if (ilKodu == null || ilKodu == "") {
        return null;
    }
    const config = getConfig();
    const response = await fetch(
        `${config.apiBaseUrl}/Hasar/HasarIlceGetir`,
        {
            method: "POST",
            headers: {
                "Authorization": `Bearer ${token}`,
                "Content-Type": "application/json"
            },
            body: JSON.stringify({ ilKodu: ilKodu.toString(), tip: tip })
        }
    );
    if (!response.ok) throw new Error("İlçe listesi alınamadı");
    return await response.json();
};

const fetchKimlikSorgula = async (token, kimlikTipi, kimlikNo) => {
    const config = getConfig();
    const response = await fetch(
        `${config.apiBaseUrl}/Hasar/KimlikSorgula`,
        {
            method: "POST",
            headers: {
                "Authorization": `Bearer ${token}`,
                "Content-Type": "application/json"
            },
            body: JSON.stringify({ kimlikTipi, kimlikNo })
        }
    );
    if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || "Kimlik bilgisi alınamadı");
    }
    return await response.json();
};
const hasarNedenleri = [
    { HASAR_KOD: "003", ACIKLAMA: "YANMA", TeminatKodu: "OK4" },
    { HASAR_KOD: "004", ACIKLAMA: "RADYO-TEYP CALINMASI", TeminatKodu: "OK5" },
    { HASAR_KOD: "005", ACIKLAMA: "CAM KIRILMASI", TeminatKodu: "OK1" },
    { HASAR_KOD: "006", ACIKLAMA: "DEVRILME", TeminatKodu: "OK2" },
    { HASAR_KOD: "009", ACIKLAMA: "SEL VE SU BASKINI", TeminatKodu: "SEL" },
    { HASAR_KOD: "013", ACIKLAMA: "DEPREM", TeminatKodu: "DEP" },
    { HASAR_KOD: "018", ACIKLAMA: "DOLU", TeminatKodu: "DEP" },
    { HASAR_KOD: "021", ACIKLAMA: "PARK HALI", TeminatKodu: "OK1" },
    { HASAR_KOD: "113", ACIKLAMA: "CALINMA-BULUNMA", TeminatKodu: "OK1" },
    { HASAR_KOD: "201", ACIKLAMA: "CARPMA / CARPIŞMA", TeminatKodu: "OK1" },
    { HASAR_KOD: "202", ACIKLAMA: "ARAC CALINMASI", TeminatKodu: "OK5" },
    { HASAR_KOD: "203", ACIKLAMA: "GLKHH TEROR", TeminatKodu: "OK3" },
    { HASAR_KOD: "204", ACIKLAMA: "SIGARA YANIGI", TeminatKodu: "OK4" },
    { HASAR_KOD: "205", ACIKLAMA: "ANAHTAR KAYBI", TeminatKodu: "OK1" },
    { HASAR_KOD: "206", ACIKLAMA: "KISISEL ESYA KAYBI", TeminatKodu: "OK1" },
    { HASAR_KOD: "207", ACIKLAMA: "STEPNE CALINMASI", TeminatKodu: "OK5" },
    { HASAR_KOD: "029", ACIKLAMA: "FIRTINA", TeminatKodu: "DEP" },
    { HASAR_KOD: "176", ACIKLAMA: "YAKIT KAYNAKLI HASAR", TeminatKodu: "OK1" },
    { HASAR_KOD: "032", ACIKLAMA: "KAR AGIRLIGI", TeminatKodu: "DEP" }
];

function KaskoIhbarBildirimPage() {
    const { token, servisTipi, policeNo, setIhbarResponse } = useHasar();
    const navigate = useNavigate();
    const [form] = Form.useForm();
    const { logApiCall, logApiSuccess, logApiError } = useApiLogger();

    const [teminatKodu, setTeminatKodu] = useState("");
    const [selectedServisTipi, setSelectedServisTipi] = useState("");
    const [selectedServisIl, setSelectedServisIl] = useState();
    const [selectedIl, setSelectedIl] = useState();
    const [errorMessage, setErrorMessage] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isTamirhaneLoaded, setIsTamirhaneLoaded] = useState(false);
    const [isMaskedPhone, setIsMaskedPhone] = useState(false);
    const [disableKimlikTipi, setDisableKimlikTipi] = useState(false);
    const [disableKimlikNo, setDisableKimlikNo] = useState(false);
    const [disableSurucuAdi, setDisableSurucuAdi] = useState(false);
    const [disableSurucuSoyad, setDisableSurucuSoyad] = useState(false);
    const [disableSurucuCep, setDisableSurucuCep] = useState(false);
    const [disableSurucuEmail, setDisableSurucuEmail] = useState(false);
    const [disableSurucuYas, setDisableSurucuYas] = useState(false);
    // İl ve İlçe state'leri
    const [ilListesiState, setIlListesiState] = useState([]);
    const [hasarIlceListesiState, setHasarIlceListesiState] = useState([]);
    const [servisIlceListesiState, setServisIlceListesiState] = useState([]);
    const [isIlLoading, setIsIlLoading] = useState(false);
    const [isHasarIlceLoading, setIsHasarIlceLoading] = useState(false);
    const [isServisIlceLoading, setIsServisIlceLoading] = useState(false);
    const [isKimlikSorgulaLoading, setIsKimlikSorgulaLoading] = useState(false);
    
    // SMS Doğrulama state'leri
    const [isSmsVerified, setIsSmsVerified] = useState(false);
    const [showVerificationInput, setShowVerificationInput] = useState(false);
    const [verificationCode, setVerificationCode] = useState("");
    const [countdown, setCountdown] = useState(0);
    const [isSendingSms, setIsSendingSms] = useState(false);
    const [isVerifyingSms, setIsVerifyingSms] = useState(false);
    const [canResendSms, setCanResendSms] = useState(true);

    const filteredServisIlceler = servisIlceListesiState;
    
    // Countdown timer effect
    useEffect(() => {
        if (countdown > 0) {
            const timer = setTimeout(() => {
                setCountdown(countdown - 1);
            }, 1000);
            return () => clearTimeout(timer);
        } else if (countdown === 0 && showVerificationInput) {
            setCanResendSms(true);
        }
    }, [countdown, showVerificationInput]);
    
    useEffect(() => {
        if (!token) {
            message.error("Oturum süreniz doldu. Lütfen önce giriş yapınız.");
            navigate("/kasko");
            return;
        }
        try {
            const decoded = jwtDecode(token);
            if (decoded.exp * 1000 < Date.now()) {
                message.error("Oturum süreniz doldu. Lütfen önce giriş yapınız.");
                navigate("/kasko");
                return;
            }
        } catch {
            message.error("Oturum süreniz doldu. Lütfen önce giriş yapınız.");
            navigate("/kasko");
            return;
        }

        // Sadece ilk mount'ta form değerlerini set et
        const initialValues = {};
        if (policeNo) {
            initialValues.policeNo = policeNo;
        }

        if (servisTipi === 1) {
            initialValues.servisTipi = "anlasmali";
            setSelectedServisTipi("anlasmali");
        } else if (servisTipi === 0) {
            initialValues.servisTipi = "anlasmasiz";
            setSelectedServisTipi("anlasmasiz");
        }

        form.setFieldsValue(initialValues);
        // İl listesini yükle
        loadIlListesi();

    }, []); // Bağımlılık dizisini boşaltarak sadece ilk mount'ta çalışmasını sağlıyoruz.

    const loadIlListesi = async () => {
        setIsIlLoading(true);
        try {
            const result = await fetchIlListesi(token);
            if (result && Array.isArray(result)) {
                setIlListesiState(result);
            }
        } catch (error) {
            console.error("İl listesi yükleme hatası:", error);
            message.warning("İl listesi yüklenemedi");
        } finally {
            setIsIlLoading(false);
        }
    };

    const loadHasarIlceListesi = async (ilKodu) => {
        setIsHasarIlceLoading(true);
        try {
            const result = await fetchIlceListesi(token, ilKodu, 'H');
            if (result && Array.isArray(result)) {
                setHasarIlceListesiState(result);
            }
        } catch (error) {
            console.error("Hasar ilçe listesi yükleme hatası:", error);
            message.warning("İlçe listesi yüklenemedi");
        } finally {
            setIsHasarIlceLoading(false);
        }
    };

    const loadServisIlceListesi = async (ilKodu) => {
        setIsServisIlceLoading(true);
        try {
            const result = await fetchIlceListesi(token, ilKodu, 'T');
            if (result && Array.isArray(result)) {
                setServisIlceListesiState(result);
            }
        } catch (error) {
            console.error("Servis ilçe listesi yükleme hatası:", error);
            message.warning("Servis ilçe listesi yüklenemedi");
        } finally {
            setIsServisIlceLoading(false);
        }
    };

    // SMS Doğrulama handler'ları
    const handleSendSmsVerification = async () => {
        try {
            // Önce ilgili form alanını validate et
            await form.validateFields(['ihbarCep']);
            
            const phoneNumber = form.getFieldValue('ihbarCep');
            
            // Değerin varlığını ve içeriğini kontrol et
            if (!phoneNumber || phoneNumber.trim() === '' || phoneNumber.replace(/[^\d]/g, '').length === 0) {
                message.error("Lütfen cep telefonu numarasını giriniz");
                return;
            }
            
            const cleaned = phoneNumber.replace(/[^\d]/g, '');
            if (cleaned.length !== 11 || !cleaned.startsWith('05')) {
                message.error("Geçerli bir cep telefonu numarası giriniz");
                return;
            }
            
            setIsSendingSms(true);
            try {
                const result = await sendSmsVerification(token, cleaned);
                if (result.success) {
                    message.success("Doğrulama kodu gönderildi");
                    setShowVerificationInput(true);
                    // API'den gelen geçerlilik süresini saniyeye çevir (dakika * 60), boşsa 300 saniye (5 dakika)
                    const gecerlilikSaniye = result.data?.gecerlilikSuresi ? result.data.gecerlilikSuresi * 60 : 300;
                    setCountdown(gecerlilikSaniye);
                    setCanResendSms(false);
                } else {
                    message.error(result.message || "SMS gönderilemedi");
                }
            } catch (error) {
                console.error("SMS gönderme hatası:", error);
                message.error("SMS gönderilemedi");
            } finally {
                setIsSendingSms(false);
            }
        } catch (validationError) {
            // Form validasyon hatası
            console.error("Validation hatası:", validationError);
            message.error("Lütfen geçerli bir cep telefonu numarası giriniz");
        }
    };
    
    const handleVerifySmsCode = async () => {
        const phoneNumber = form.getFieldValue('ihbarCep');
        const cleaned = phoneNumber.replace(/[^\d]/g, '');
        
        if (!verificationCode || verificationCode.length !== 4) {
            message.error("Lütfen 4 haneli doğrulama kodunu giriniz");
            return;
        }
        
        setIsVerifyingSms(true);
        try {
            const result = await verifySmsCode(token, cleaned, verificationCode);
            if (result.success && result.data?.isValid) {
                message.success("Telefon numarası doğrulandı");
                setIsSmsVerified(true);
                setShowVerificationInput(false);
                setVerificationCode("");
                setCountdown(0);
            } else {
                const remainingAttempts = result.data?.remainingAttempts;
                if (remainingAttempts !== undefined) {
                    message.error(`Doğrulama kodu hatalı. Kalan deneme hakkı: ${remainingAttempts}`);
                } else {
                    message.error(result.message || "Doğrulama kodu hatalı");
                }
                if (remainingAttempts === 0) {
                    setShowVerificationInput(false);
                    setVerificationCode("");
                    setCountdown(0);
                    setCanResendSms(true);
                }
            }
        } catch (error) {
            console.error("SMS doğrulama hatası:", error);
            message.error("SMS doğrulaması yapılamadı");
        } finally {
            setIsVerifyingSms(false);
        }
    };
    
    // Sürücü kimlik sorgulama handler
    const handleSurucuKimlikSorgula = async (kimlikTipi, kimlikNo) => {
        //if (!kimlikNo || kimlikNo.length < 10) {
        //    return;
        //}

        // Kimlik tipi mapping
        const kimlikTipiMap = {
            "1": "TCKN",    // TC Kimlik No
            "4": "YKN"      // Yabancı Kimlik
        };

        const mappedKimlikTipi = kimlikTipiMap[kimlikTipi];
        if (!mappedKimlikTipi) {
            return;
        }

        setIsKimlikSorgulaLoading(true);
        try {
            const result = await fetchKimlikSorgula(token, mappedKimlikTipi, kimlikNo);

            // TC Kimlik veya Yabancı Kimlik için
            if ((mappedKimlikTipi === "TCKN" || mappedKimlikTipi === "YKN") && result) {
                form.setFieldsValue({
                    surucuAdi: result.adi || "",
                    surucuSoyad: result.soyadi || ""
                });
                message.success("Sürücü kimlik bilgisi başarıyla getirildi");
            }

        } catch (error) {
            console.error("Sürücü Kimlik Sorgulama Hatası:", error);
            message.warning("Sürücü kimlik bilgisi alınamadı");
            form.setFieldsValue({
                kimlikNo: ""
            });
        } finally {
            setIsKimlikSorgulaLoading(false);
        }
    };

    // Servis kimlik sorgulama handler
    const handleServisKimlikSorgula = async (kimlikNo) => {
        if (!kimlikNo || kimlikNo.length < 10) {
            return;
        }

        // Karakter uzunluğuna göre kimlik tipini belirle
        let mappedKimlikTipi = null;
        if (kimlikNo.length === 10) {
            mappedKimlikTipi = "VKN"; // Vergi No
        } else if (kimlikNo.length === 11) {
            mappedKimlikTipi = "TCKN"; // TC Kimlik No
        } else {
            return; // Geçersiz uzunluk
        }

        setIsKimlikSorgulaLoading(true);
        try {
            const result = await fetchKimlikSorgula(token, mappedKimlikTipi, kimlikNo);

            // TC Kimlik için
            if (mappedKimlikTipi === "TCKN" && result) {
                const adSoyad = `${result.adi || ""} ${result.soyadi || ""}`.trim();
                form.setFieldsValue({
                    servisAdSoyad: adSoyad
                });
                message.success("Servis TC kimlik bilgisi başarıyla getirildi");
            }
            // Vergi No için
            else if (mappedKimlikTipi === "VKN" && result) {
                form.setFieldsValue({
                    servisAdSoyad: result.unvan || ""
                });
                message.success("Servis vergi kimlik bilgisi başarıyla getirildi");
            }

        } catch (error) {
            console.error("Servis Kimlik Sorgulama Hatası:", error);
            message.warning("Servis kimlik bilgisi alınamadı");
            form.setFieldsValue({
                servisVergiNo: ""
            });
        } finally {
            setIsKimlikSorgulaLoading(false);
        }
    };

    const handleFormValuesChange = useCallback((changedValues) => {
        if (changedValues.hasOwnProperty('servisTipi')) {
            const newValue = changedValues.servisTipi;
            setSelectedServisTipi(newValue);

            // Servis tipi değiştiğinde ilgili alanları temizle
            form.resetFields([
                'servisKodu',
                'servisAdSoyad',
                'servisIlKodu',
                'servisIlceKodu',
                'servisAdres',
                'servisTelefon',
                'servisVergiNo'
            ]);
            setSelectedServisIl(undefined);
            setIsTamirhaneLoaded(false);
        }
    }, [form]);

    //// Filter ilceListesi for the selected servis il
    //const filteredServisIlceler = ilceListesi.filter(x => x.IL_KOD === selectedServisIl);

    // Seçilen ile göre ilçeleri filtrele ve alfabetik sırala
    const filteredIlceler = hasarIlceListesiState
        .sort((a, b) => (a.ilceAdi || a.ilceAd || '').localeCompare((b.ilceAdi || b.ilceAd || ''), "tr", { sensitivity: "base" }));

    const handleSubmit = async (values) => {
        // SMS doğrulama kontrolü
        if (!isSmsVerified) {
            message.error("Lütfen önce cep telefonu numaranızı doğrulayınız");
            return;
        }
        
        // Yurtdışı kontrolü
        if (values.hasarIl === "0") {
            message.warning({
                content: "Yurtdışında oluşan hasar için dosyanızı Çağrı Merkezini arayarak açtırabilirsiniz",
                duration: 10,
            });
            return;
        }

        // Anlaşmalı servis seçiliyse tamirhane kodu zorunlu kontrolü
        if (values.servisTipi === "anlasmali" && !values.servisKodu && !values.servisAdSoyad) {
            message.error("Anlaşmalı servis için servis kodu zorunludur!");
            return;
        }

        // Her istekte hata mesajını sıfırla
        setErrorMessage("");
        setIsSubmitting(true);

        try {
            const cleanPhone = (phone) => {
                if (!phone) return "";
                return phone.replace(/[^\d]/g, '').replace(/^0/, '');
            };

            const isAnlasmasizTamirhane = values.servisTipi === "anlasmasiz";

            // Dosya türü mapping
            const dosyaTuruMap = {
                "Beyan": 0,
                "Zabit": 2,
                "AnlaşmalıTutanakKTT": 1
            };

            // Kazayı yapan mapping
            const kazayiYapanMap = {
                "Sigortali": 0,
                "Aile": 1,
                "Diger": 2
            };

            // Kimlik tipi mapping
            const kimlikTipMap = {
                "TcKimlikNo": 1,
                "VergiNo": 2,
                "PasaportNo": 3,
                "YabanciKimlikNo": 4,
                "Kimliksiz": 9
            };

            // API request objesi oluştur
            const requestData = {
                // Mevcut ekrandan doldurulan alanlar
                policeNo: parseInt(values.policeNo) || 0,
                hasarNedenKodu: values.hasarNedeni || "",
                teminatKodu: values.teminatKodu || "",
                hasarTarihVeSaat: values.hasarTarihi
                    ? values.hasarTarihi.format('YYYY-MM-DDTHH:mm:ss') // UTC yerine yerel saat
                    : dayjs().format('YYYY-MM-DDTHH:mm:ss'),
                hasarIlKodu: values.hasarIl || "",
                hasarIlceKodu: values.hasarIlce || "",
                dosyaTuru: dosyaTuruMap[values.dosyaTuru] ?? 0,
                tahminiTazminat: parseFloat(values.tahminiTutar) || 0,

                // İhbar yapan bilgileri - Mevcut "İhbar Eden" alanlarından
                ihbarYapanTipi: "1",  // Ekranda yok
                ihbarYapanKimlikTip: "",  // Ekranda yok
                ihbarYapanKimlikNo: "",  // Ekranda yok
                ihbarYapanAd: values.ihbarAdSoyad ? values.ihbarAdSoyad.split(' ')[0] : "",
                ihbarYapanSoyad: values.ihbarAdSoyad ? values.ihbarAdSoyad.split(' ').slice(1).join(' ') : "",
                cepTelefon: cleanPhone(values.ihbarCep),
                sabitTelefon: cleanPhone(values.ihbarSabit),
                ihbarYapanEmail: values.ihbarEmail || null,

                // Sürücü bilgileri - Mevcut "Sürücü Bilgileri" alanlarından
                kazayiYapan: kazayiYapanMap[values.surucuKim] ?? 0,
                surucuAd: values.surucuAdi || "",
                surucuSoyad: values.surucuSoyad || "",
                surucuKimlikNo: values.kimlikNo || "",
                surucuKimlikTip: kimlikTipMap[values.kimlikTipi] ?? 1,
                surucuTelefon: cleanPhone(values.surucuCep),
                surucuEposta: values.surucuEmail || null,
                adSoyad: values.ihbarAdSoyad || "",
                araciKullananYas: parseInt(values.surucuYas) || 30,

                // Tamirhane bilgileri - Mevcut "Servis Bilgileri" alanlarından
                tamireVerilisTarih: dayjs().format('YYYY-MM-DD'), // UTC yerine yerel saat

                // Kasko için bu alanlar her zaman null/false
                ikiliMi: false,
                hasarOlusNedeni: null,

                // Mağdur bilgileri - Kasko için hepsi null
                magdurAd: null,
                magdurSoyad: null,
                magdurKimlikNo: null,
                magdurKimlikTipi: 66,
                magdurCepTelefon: null,
                magdurEmail: null,
                magdurTescilBelgeSeriNo: null,
                magdurPlaka: null,
                magdurMarkaKod: null,
                magdurMarkaAd: null,
                magdurModelYil: null,
                magdurAracTipi: null,
                magdurAracTuruKod: null,
                magdurAracTuruAd: null,
                magdurMotorNo: null,
                magdurSasiNo: null,
                magdurHesapIBAN: null,
                magdurHesapSahibiAdi: null,
                magdurHesapSahibiSoyadi: null,

                magdurHesapIBAN: null,
                magdurHesapSahibiAdi: null,
                magdurHesapSahibiSoyadi: null
            };

            if (values.isVekalet || values.hesapSahibiAdi || values.hesapSahibiSoyadi || values.hesapSahibiIban) {
                requestData.ekBilgi = {
                    isVekalet: values.isVekalet ? "true" : "false",
                    HesapSahibiAdi: values.hesapSahibiAdi || "",
                    HesapSahibiSoyadi: values.hesapSahibiSoyadi || "",
                    HesapSahibiIban: values.hesapSahibiIban ? values.hesapSahibiIban.replace(/\s/g, '').toUpperCase() : ""
                };
            }

            // Anlaşmalı tamirhane ise sadece servis kodu gönder
            if (!isAnlasmasizTamirhane) {
                requestData.tamirhaneKodu = values.servisKodu || "";
                requestData.tamirhaneAdSoyadUnvan = null;
                requestData.tamirhaneAdres = null;
                requestData.tamirhaneIlKodu = null;
                requestData.tamirhaneIlceKodu = null;
                requestData.tamirhaneTelefon = null;
                requestData.tamirhaneKimlikNo = null;
            } else {
                // Anlaşmasız tamirhane ise tüm bilgileri gönder
                requestData.tamirhaneKodu = null;
                requestData.tamirhaneAdSoyadUnvan = values.servisAdSoyad || "";
                requestData.tamirhaneAdres = values.servisAdres || "";
                requestData.tamirhaneIlKodu = values.servisIlKodu || "";
                requestData.tamirhaneIlceKodu = values.servisIlceKodu || "";
                requestData.tamirhaneTelefon = cleanPhone(values.servisTelefon);
                requestData.tamirhaneKimlikNo = values.servisVergiNo || "";
            }
            // API'ye istek gönder
            const result = await submitKaskoIhbar(token, requestData, logApiCall, logApiSuccess, logApiError);

            // Response'u context'e kaydet
            setIhbarResponse({
                eksperNot: result.eksperNot || "",
                eksperTel: result.eksperTel || "",
                eksperWp: result.eksperWp || "",
                eksperMail: result.eksperMail || "",
                dosyaEksperKodu: result.dosyaEksperKodu || "",
                dosyaEksperi: result.dosyaEksperi || "",
                dosyaNo: result.dosyaNo || null,
                dosyaSorumlusu: result.dosyaSorumlusu || "",
                messages: result.messages || null,
                anlasmasizTamirhane: isAnlasmasizTamirhane

            });

            message.success("Hasar dosyası başarı ile açıldı.");
            // Başarılı işlem sonrası başarı sayfasına yönlendir
            setTimeout(() => {
                navigate("/kasko/ihbar/basarili");
            }, 1000);

        } catch (error) {
            console.error("Hata:", error);

            // 401 hatası için özel kontrol
            if (error.message && error.message.includes("401")) {
                message.error("Oturumunuz sona erdi. Lütfen tekrar giriş yapın.");
                navigate("/kasko");
                return;
            }

            let errorMsg = "Kasko ihbar bildirimi kaydedilemedi!";
            let callId = "";

            // Error message'ı parse et ve callId'yi çıkar
            try {
                const errorText = error.message || "";

                // CallId'yi mesajdan ayır (errorMessage_callId formatında)
                if (errorText.includes('_')) {
                    const parts = errorText.split('_');
                    callId = parts[parts.length - 1]; // Son parça callId
                    const messageWithoutCallId = parts.slice(0, -1).join('_'); // Geri kalan mesaj

                    // JSON formatında mı kontrol et
                    if (messageWithoutCallId.includes('{') && messageWithoutCallId.includes('}')) {
                        const jsonMatch = messageWithoutCallId.match(/\{.*\}/);
                        if (jsonMatch) {
                            const errorJson = JSON.parse(jsonMatch[0]);
                            errorMsg = errorJson.error || errorJson.message || messageWithoutCallId;
                        }
                    } else {
                        errorMsg = messageWithoutCallId;
                    }
                } else {
                    errorMsg = errorText;
                }
            } catch (parseError) {
                // Parse edilemezse direkt mesajı kullan
                errorMsg = error.message || errorMsg;
            }
            // CallId varsa mesaja ekle
            const fullErrorMsg = callId
                ? `${errorMsg}\n\nReferans No: ${callId}`
                : errorMsg;

            setErrorMessage(fullErrorMsg);
            message.error({
                content: fullErrorMsg,
                duration: 10, // Daha uzun süre göster
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    //// Seçilen ile göre ilçeleri filtrele ve alfabetik sırala
    //const filteredIlceler = ilceListesi
    //    .filter(x => x.IL_KOD === selectedIl)
    //    .sort((a, b) => a.ILCE_AD.localeCompare(b.ILCE_AD, "tr", { sensitivity: "base" }));

    // Sort hasarNedenleri alphabetically by ACIKLAMA
    const sortedHasarNedenleri = [...hasarNedenleri].sort((a, b) =>
        a.ACIKLAMA.localeCompare(b.ACIKLAMA, "tr", { sensitivity: "base" })
    );

    return (
        <div className="bildirim-container">
            <h2 className="bildirim-title">KASKO Hasar İhbar Bildirimi</h2>
            <ConfigProvider locale={trTR}>
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleSubmit}
                    onValuesChange={handleFormValuesChange}
                    autoComplete="off"
                    scrollToFirstError={{
                        behavior: 'smooth',
                        block: 'center',
                    }}
                >
                    <Collapse defaultActiveKey={["1", "2", "3", "4", "5"]} bordered={true} className="bildirim-collapse">
                        <Panel header="Hasar Bilgileri" key="1">
                            <Row gutter={16}>
                                <Col xs={24} md={12}>
                                    <Form.Item label="Poliçe Numarası" name="policeNo" rules={[{ required: true, message: "Bu alan zorunludur" }]}>
                                        <Input disabled />
                                    </Form.Item>
                                </Col>


                            </Row>
                            <Row gutter={16}>
                                <Col xs={24} md={12}>
                                    <Form.Item label="Hasar Nedeni" name="hasarNedeni" rules={[{ required: true, message: "Bu alan zorunludur" }]}>
                                        <Select
                                            allowClear
                                            showSearch
                                            optionFilterProp="children"
                                            filterOption={turkishSearch}
                                            onChange={value => {
                                                const secilen = hasarNedenleri.find(x => x.HASAR_KOD === value);
                                                if (secilen) {
                                                    setTeminatKodu(secilen.TeminatKodu);
                                                    form.setFieldsValue({ teminatKodu: secilen.TeminatKodu });
                                                }
                                            }}
                                        >
                                            {sortedHasarNedenleri.map(item => (
                                                <Option key={item.HASAR_KOD} value={item.HASAR_KOD}>
                                                    {item.ACIKLAMA}
                                                </Option>
                                            ))}
                                        </Select>
                                    </Form.Item>
                                    <Form.Item name="teminatKodu" style={{ display: "none" }}>
                                        <Input />
                                    </Form.Item>
                                </Col>
                                <Col xs={24} md={12}>
                                    <Form.Item label="Hasar Tarihi ve Saati" name="hasarTarihi" rules={[{ required: true, message: "Bu alan zorunludur" }]}>
                                        <DatePicker showTime format="DD/MM/YYYY HH:mm" style={{ width: "100%" }} />
                                    </Form.Item>
                                </Col>


                            </Row>
                            <Row gutter={16}>

                                <Col xs={24} md={12}>
                                    <Form.Item label="Hasar İli" name="hasarIl" rules={[{ required: true, message: "Bu alan zorunludur" }]}>
                                        <Select
                                            allowClear
                                            showSearch
                                            optionFilterProp="children"
                                            filterOption={turkishSearch}
                                            onChange={val => {
                                                if (val === "0") {
                                                    message.warning({
                                                        content: "Yurtdışında oluşan hasar için dosyanızı Çağrı Merkezini arayarak açtırabilirsiniz",
                                                        duration: 5,
                                                    });
                                                    return;
                                                }
                                                setSelectedIl(val);
                                                form.setFieldsValue({ hasarIlce: undefined });
                                                // İlçeleri yükle
                                                loadHasarIlceListesi(val);
                                            }}
                                        >
                                            {ilListesiState.map(il => (
                                                <Option key={il.ilKod || il.ilKodu} value={il.ilKod || il.ilKodu}>
                                                    {il.ilAdi || il.ilAd}
                                                </Option>
                                            ))}
                                        </Select>
                                    </Form.Item>
                                </Col>
                                <Col xs={24} md={12}>
                                    <Form.Item label="Hasar İlçesi" name="hasarIlce" rules={[{ required: true, message: "Bu alan zorunludur" }]}>
                                        <Select allowClear
                                            showSearch
                                            optionFilterProp="children"
                                            loading={isHasarIlceLoading}
                                            filterOption={turkishSearch}
                                            disabled={!selectedIl}>
                                            {filteredIlceler.map(ilce => (
                                                <Option key={ilce.ilceKod || ilce.ilceKodu} value={ilce.ilceKod || ilce.ilceKodu}>
                                                    {ilce.ilceAdi || ilce.ilceAd}
                                                </Option>
                                            ))}
                                        </Select>
                                    </Form.Item>
                                </Col>

                            </Row>
                            <Row gutter={16}>
                                <Col xs={24} md={12}>
                                    <Form.Item
                                        label="Tahmini Hasar Tutarı"
                                        name="tahminiTutar"
                                        rules={[
                                            { required: true, message: "Tahmini tutarı giriniz" }
                                        ]}
                                    >
                                        <InputNumber
                                            style={{ width: "100%" }}
                                            formatter={value => {
                                                if (!value) return '';
                                                // Sayıyı string'e çevir ve virgülden ayır
                                                const parts = value.toString().split('.');
                                                // Tam kısmı binlik ayraçla formatla
                                                parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.');
                                                // Küsürat varsa virgül ile birleştir
                                                return parts.length > 1 ? `${parts[0]},${parts[1].slice(0, 2)}` : parts[0];
                                            }}
                                            parser={value => {
                                                if (!value) return '';
                                                // Binlik ayraçları temizle ve virgülü noktaya çevir
                                                return value.replace(/\./g, '').replace(',', '.');
                                            }}
                                            decimalSeparator=","
                                            precision={2}
                                            step={0.01}
                                            min={0}
                                            max={999999999.99}
                                            placeholder="Tutarı TL olarak giriniz (örn: 5.000,50)"
                                            controls={false}
                                            // Ondalık kısmı 2 basamakla sınırla
                                            onBlur={(e) => {
                                                const value = form.getFieldValue('tahminiTutar');
                                                if (value) {
                                                    // Değeri 2 ondalık basamağa yuvarla
                                                    const rounded = Math.round(value * 100) / 100;
                                                    form.setFieldValue('tahminiTutar', rounded);
                                                }
                                            }}
                                        />
                                    </Form.Item>
                                </Col>
                                <Col xs={24} md={12}>
                                    <Form.Item label="Dosya Türü" name="dosyaTuru" rules={[{ required: true, message: "Bu alan zorunludur" }]}>
                                        <Select
                                            allowClear
                                            showSearch
                                            optionFilterProp="children"
                                            filterOption={turkishSearch}>
                                            <Option value="Beyan">Beyan</Option>
                                            <Option value="Zabit">Zabıt</Option>
                                            <Option value="AnlaşmalıTutanakKTT">Anlaşmalı Tutanak (KTT)</Option>
                                        </Select>
                                    </Form.Item>
                                </Col>
                            </Row>
                        </Panel>
                        <Panel header="İhbar Eden Bilgileri" key="2">
                            <Row gutter={16}>
                                <Col xs={24} md={12}>
                                    <Form.Item label="Adı ve Soyadı" name="ihbarAdSoyad" rules={[{ required: true, message: "Bu alan zorunludur" }]}>
                                        <Input />
                                    </Form.Item>
                                </Col>
                                <Col xs={24} md={12}>
                                    <Form.Item
                                        label="Email"
                                        name="ihbarEmail"
                                        rules={[
                                            { required: true, message: "Bu alan zorunludur" },
                                            {
                                                validator: (_, value) => {
                                                    if (!value) return Promise.resolve();
                                                    const email = value.trim();
                                                    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

                                                    if (emailRegex.test(email)) {
                                                        return Promise.resolve();
                                                    }

                                                    return Promise.reject(new Error('Geçerli bir email adresi giriniz'));
                                                }
                                            }
                                        ]}
                                    >
                                        <Input placeholder="ornek@email.com" />
                                    </Form.Item>
                                </Col>
                            </Row>
                            <Row gutter={16}>
                                <Col xs={24} md={12}>
                                    <Form.Item
                                        label={(
                                            <span>
                                                Cep Telefonu
                                                {isSmsVerified && (
                                                    <span style={{ marginLeft: 8, color: '#52c41a', fontSize: '12px' }}>
                                                        ✓ Doğrulandı
                                                    </span>
                                                )}
                                            </span>
                                        )}
                                        required
                                    >
                                        <div style={{ display: 'flex', width: '100%', overflow: 'hidden' }}>
                                            <Form.Item
                                                name="ihbarCep"
                                                noStyle
                                                rules={[
                                                    { required: true, message: "Cep telefonu giriniz" },
                                                    {
                                                        validator: (_, value) => {
                                                            if (!value) return Promise.resolve();
                                                            const cleaned = value.replace(/[^\d]/g, '');
                                                            if (cleaned.length === 11 && cleaned.startsWith('05')) {
                                                                return Promise.resolve();
                                                            }
                                                            return Promise.reject(new Error('Cep telefonu 05 ile başlamalı ve 11 haneli olmalıdır'));
                                                        }
                                                    }
                                                ]}
                                            >
                                                <InputMask
                                                    mask="0(599) 999 99 99"
                                                    maskChar={null}
                                                    formatChars={{
                                                        '9': '[0-9]',
                                                        '5': '[0-9]'
                                                    }}
                                                    disabled={isSmsVerified}
                                                >
                                                    {(inputProps) => (
                                                        <Input
                                                            {...inputProps}
                                                            placeholder="0(5__) ___ __ __"
                                                            maxLength={16}
                                                            style={{ 
                                                                width: isSmsVerified ? '100%' : 'calc(100% - 90px)',
                                                                borderTopRightRadius: isSmsVerified ? undefined : 0, 
                                                                borderBottomRightRadius: isSmsVerified ? undefined : 0 
                                                            }}
                                                            onKeyPress={(e) => {
                                                                const value = e.currentTarget.value.replace(/[^\d]/g, '');
                                                                if (value.length >= 11) {
                                                                    e.preventDefault();
                                                                }
                                                            }}
                                                        />
                                                    )}
                                                </InputMask>
                                            </Form.Item>
                                            {!isSmsVerified && (
                                                <Button
                                                    type="primary"
                                                    style={{ 
                                                        width: '90px',
                                                        minWidth: '90px',
                                                        maxWidth: '90px',
                                                        borderTopLeftRadius: 0, 
                                                        borderBottomLeftRadius: 0,
                                                        fontVariantNumeric: 'tabular-nums'
                                                    }}
                                                    onClick={handleSendSmsVerification}
                                                    loading={isSendingSms}
                                                    disabled={!canResendSms || countdown > 0}
                                                >
                                                    {countdown > 0 ? `${Math.floor(countdown / 60)}:${(countdown % 60).toString().padStart(2, '0')}` : 'Doğrula'}
                                                </Button>
                                            )}
                                        </div>
                                    </Form.Item>
                                    {showVerificationInput && !isSmsVerified && (
                                        <Form.Item
                                            label="Doğrulama Kodu"
                                            help={<span style={{ fontVariantNumeric: 'tabular-nums' }}>{`Kalan süre: ${Math.floor(countdown / 60)}:${(countdown % 60).toString().padStart(2, '0')}`}</span>}
                                        >
                                            <div style={{ display: 'flex', width: '100%', overflow: 'hidden' }}>
                                                <Input
                                                    value={verificationCode}
                                                    onChange={(e) => {
                                                        const value = e.target.value.replace(/[^\d]/g, '');
                                                        if (value.length <= 4) {
                                                            setVerificationCode(value);
                                                        }
                                                    }}
                                                    placeholder="4 haneli kodu giriniz"
                                                    maxLength={4}
                                                    style={{ width: 'calc(100% - 90px)', borderTopRightRadius: 0, borderBottomRightRadius: 0 }}
                                                    onPressEnter={handleVerifySmsCode}
                                                />
                                                <Button
                                                    type="primary"
                                                    style={{ 
                                                        width: '90px', 
                                                        minWidth: '90px',
                                                        maxWidth: '90px',
                                                        borderTopLeftRadius: 0, 
                                                        borderBottomLeftRadius: 0 
                                                    }}
                                                    onClick={handleVerifySmsCode}
                                                    loading={isVerifyingSms}
                                                    disabled={verificationCode.length !== 4}
                                                >
                                                    Onayla
                                                </Button>
                                            </div>
                                        </Form.Item>
                                    )}
                                </Col>
                                <Col xs={24} md={12}>
                                    <Form.Item
                                        label="Sabit Telefonu"
                                        name="ihbarSabit"
                                        rules={[
                                            {
                                                validator: (_, value) => {
                                                    if (!value) return Promise.resolve();
                                                    const cleaned = value.replace(/[^\d]/g, '');
                                                    if (cleaned.length === 11) {
                                                        return Promise.resolve();
                                                    }
                                                    return Promise.reject(new Error('Sabit telefon 11 haneli olmalıdır'));
                                                }
                                            }
                                        ]}
                                    >
                                        <InputMask
                                            mask="0(999) 999 99 99"
                                            maskChar={null}
                                            formatChars={{
                                                '9': '[0-9]'
                                            }}
                                        >
                                            {(inputProps) => (
                                                <Input
                                                    {...inputProps}
                                                    placeholder="0(___) ___ __ __"
                                                    maxLength={16}
                                                    onKeyPress={(e) => {
                                                        const value = e.currentTarget.value.replace(/[^\d]/g, '');
                                                        if (value.length >= 11) {
                                                            e.preventDefault();
                                                        }
                                                    }}
                                                />
                                            )}
                                        </InputMask>
                                    </Form.Item>

                                </Col>
                            </Row>
                        </Panel>
                        <Panel header="Sürücü Bilgileri (Zurich Sigorta)" key="3">
                            <Row gutter={16}>
                                <Col xs={24} md={12}>
                                    <Form.Item label="Kazayı Yapan Kim?" name="surucuKim" rules={[{ required: true, message: "Bu alan zorunludur" }]}>
                                        <Select
                                            allowClear
                                            onChange={async (value) => {
                                                if (value == "Sigortali") {
                                                    try {
                                                        const data = await fetchSigortaliBilgisi(policeNo, token);
                                                        if (data.tcKimlikNo != null && data.tcKimlikNo != "") {
                                                            const phoneNumber = data.gsm || data.tel || "";
                                                            const hasMask = phoneNumber.includes('*');
                                                            setIsMaskedPhone(hasMask);

                                                            setDisableKimlikTipi(!!data.tcKimlikNo);
                                                            setDisableKimlikNo(!!data.tcKimlikNo);
                                                            setDisableSurucuAdi(!!data.mustAdi);
                                                            setDisableSurucuSoyad(!!data.mustSoyadi);
                                                            setDisableSurucuCep(!!phoneNumber);
                                                            setDisableSurucuEmail(!!data.eMail);
                                                            setDisableSurucuYas(!!data.yas);

                                                            form.setFieldsValue({
                                                                kimlikTipi: data.tcKimlikNo ? "TcKimlikNo" : "",
                                                                kimlikNo: data.tcKimlikNo || "",
                                                                surucuAdi: data.mustAdi || "",
                                                                surucuSoyad: data.mustSoyadi || "",
                                                                surucuCep: phoneNumber,
                                                                surucuEmail: data.eMail || "",
                                                                surucuYas: data.yas || ""
                                                            });
                                                        }
                                                    } catch (err) {
                                                        message.error("Sigortalı bilgisi alınamadı");
                                                        setDisableKimlikTipi(false);
                                                        setDisableKimlikNo(false);
                                                        setDisableSurucuAdi(false);
                                                        setDisableSurucuSoyad(false);
                                                        setDisableSurucuCep(false);
                                                        setDisableSurucuEmail(false);
                                                        setDisableSurucuYas(false);
                                                    }
                                                } else {
                                                    setIsMaskedPhone(false);
                                                    setDisableKimlikTipi(false);
                                                    setDisableKimlikNo(false);
                                                    setDisableSurucuAdi(false);
                                                    setDisableSurucuSoyad(false);
                                                    setDisableSurucuCep(false);
                                                    setDisableSurucuEmail(false);
                                                    setDisableSurucuYas(false);
                                                    form.setFieldsValue({
                                                        kimlikTipi: "",
                                                        kimlikNo: "",
                                                        surucuAdi: "",
                                                        surucuSoyad: "",
                                                        surucuCep: "",
                                                        surucuEmail: "",
                                                        surucuYas: ""
                                                    });
                                                }
                                            }}
                                        >
                                            <Option value="Sigortali">Sigortalı</Option>
                                            <Option value="Aile">Ailesi</Option>
                                            <Option value="Diger">Diğer</Option>
                                        </Select>
                                    </Form.Item>
                                </Col>
                                <Col xs={24} md={12}>
                                    <Form.Item label="Kimlik Tipi" name="kimlikTipi" rules={[{ required: true, message: "Bu alan zorunludur" }]}>
                                        <Select allowClear disabled={disableKimlikTipi}
                                            onChange={(value) => {
                                                // Pasaport seçildiğinde bilgilendirme mesajı göster
                                                if (value === "PasaportNo") {
                                                    message.info({
                                                        content: "Pasaport numarası sadece rakam olarak girilmelidir",
                                                        duration: 4,
                                                    });
                                                }
                                            }}>
                                            <Option value="TcKimlikNo">TC Kimlik Numarası</Option>
                                            <Option value="YabanciKimlikNo">Yabancı Kimlik Numarası</Option>
                                            <Option value="PasaportNo">Pasaport Numarası</Option>
                                        </Select>
                                    </Form.Item>
                                </Col>
                            </Row>
                            <Row gutter={16}>
                                <Col xs={24} md={12}>
                                    <Form.Item
                                        label="Kimlik No"
                                        name="kimlikNo"
                                        rules={[
                                            { required: true, message: "Bu alan zorunludur" },
                                            {
                                                validator: (_, value) => {
                                                    const kimlikTipi = form.getFieldValue('kimlikTipi');
                                                    if (!value) return Promise.resolve();

                                                    // Pasaport numarası için sadece rakam kontrolü
                                                    if (kimlikTipi === "PasaportNo") {
                                                        const onlynumbers = /^[0-9]+$/;
                                                        if (!onlynumbers.test(value)) {
                                                            return Promise.reject(new Error('Pasaport numarası sadece rakam olarak girilmelidir'));
                                                        }
                                                    }

                                                    return Promise.resolve();
                                                }
                                            }
                                        ]}
                                    >
                                        <Input
                                            maxLength={11}
                                            disabled={disableKimlikNo}
                                            onKeyPress={(e) => {
                                                const kimlikTipi = form.getFieldValue('kimlikTipi');
                                                // Pasaport seçili ise sadece rakam girişine izin ver
                                                if (kimlikTipi === "PasaportNo") {
                                                    const charCode = e.which ? e.which : e.keyCode;
                                                    if (charCode > 31 && (charCode < 48 || charCode > 57)) {
                                                        e.preventDefault();
                                                    }
                                                }
                                            }}
                                            onBlur={async (e) => {
                                                const kimlikNo = e.target.value;
                                                const kimlikTipi = form.getFieldValue('kimlikTipi');

                                                // Disabled değilse ve TC Kimlik, Vergi No veya Yabancı Kimlik seçiliyse API'yi çağır
                                                if (!disableKimlikNo && kimlikNo) {
                                                    let apiKimlikTipi = null;

                                                    if (kimlikTipi === "TcKimlikNo") {
                                                        apiKimlikTipi = "1"; // TC Kimlik
                                                    } else if (kimlikTipi === "YabanciKimlikNo") {
                                                        apiKimlikTipi = "4"; // Yabancı Kimlik
                                                    }

                                                    if (apiKimlikTipi) {
                                                        await handleSurucuKimlikSorgula(apiKimlikTipi, kimlikNo);
                                                    }
                                                }
                                            }}
                                        />
                                    </Form.Item>
                                </Col>
                                <Col xs={24} md={12}>
                                    <Form.Item label="Sürücü Adı" name="surucuAdi" rules={[{ required: true, message: "Bu alan zorunludur" }]}>
                                        <Input disabled={disableSurucuAdi} />
                                    </Form.Item>
                                </Col>
                            </Row>
                            <Row gutter={16}>
                                <Col xs={24} md={12}>
                                    <Form.Item label="Sürücü Soyadı" name="surucuSoyad" rules={[{ required: true, message: "Bu alan zorunludur" }]}>
                                        <Input disabled={disableSurucuSoyad} />
                                    </Form.Item>
                                </Col>
                                <Col xs={24} md={12}>
                                    <Form.Item
                                        label="Sürücü Cep Telefonu"
                                        name="surucuCep"
                                        rules={[
                                            { required: true, message: "Bu alan zorunludur" },
                                            {
                                                validator: (_, value) => {
                                                    if (!value) return Promise.resolve();
                                                    if (isMaskedPhone) return Promise.resolve();
                                                    const cleaned = value.replace(/[^\d]/g, '');
                                                    if (cleaned.length === 11 && cleaned.startsWith('05')) {
                                                        return Promise.resolve();
                                                    }
                                                    return Promise.reject(new Error('Cep telefonu 05 ile başlamalı ve 11 haneli olmalıdır'));
                                                }
                                            }
                                        ]}
                                    >
                                        {isMaskedPhone ? (
                                            <Input disabled placeholder="Maskeli telefon" />
                                        ) : (
                                            <InputMask
                                                mask="0(599) 999 99 99"
                                                maskChar={null}
                                                formatChars={{
                                                    '9': '[0-9]',
                                                    '5': '[0-9]'
                                                }}
                                                disabled={disableSurucuCep}
                                            >
                                                {(inputProps) => (
                                                    <Input
                                                        {...inputProps}
                                                        placeholder="0(5__) ___ __ __"
                                                        maxLength={16}
                                                        onKeyPress={(e) => {
                                                            const value = e.currentTarget.value.replace(/[^\d]/g, '');
                                                            if (value.length >= 11) {
                                                                e.preventDefault();
                                                            }
                                                        }}
                                                    />
                                                )}
                                            </InputMask>
                                        )}
                                    </Form.Item>
                                </Col>
                            </Row>
                            <Row gutter={16}>
                                <Col xs={24} md={12}>
                                    <Form.Item label="Sürücü Email" name="surucuEmail">
                                        <Input disabled={disableSurucuEmail} />
                                    </Form.Item>
                                </Col>
                                <Col xs={24} md={12}>
                                    <Form.Item label="Kazayı Yapan Kişinin Yaşı" name="surucuYas">
                                        <Input disabled={disableSurucuYas} />
                                    </Form.Item>
                                </Col>
                            </Row>
                        </Panel>
                        <Panel header="Servis Bilgileri" key="4">
                            <Row gutter={16}>
                                <Col xs={24}>
                                    <Form.Item label="Servis Tipi" name="servisTipi" rules={[{ required: true, message: "Bu alan zorunludur" }]}>
                                        <Radio.Group>
                                            <Radio value="anlasmali">Anlaşmalı Servis</Radio>
                                            <Radio value="anlasmasiz">Anlaşmasız Servis</Radio>
                                        </Radio.Group>
                                    </Form.Item>
                                </Col>
                            </Row>
                            <Row gutter={16}>
                                <Col xs={24} md={12}>
                                    <Form.Item
                                        label={
                                            selectedServisTipi === "anlasmali" ? (
                                                <span>
                                                    Servis Kodu&nbsp;
                                                    <Tooltip title="hasarlojistik@zurich.com mail adresinden tamirhane kodunuzu öğrenebilirsiniz.">
                                                        <InfoCircleOutlined style={{ color: "#1890ff" }} />
                                                    </Tooltip>
                                                </span>
                                            ) : (
                                                "Servis Kodu"
                                            )
                                        }
                                        name="servisKodu"
                                        rules={[
                                            {
                                                required: selectedServisTipi === "anlasmali",
                                                message: "Anlaşmalı servis için servis kodu zorunludur"
                                            }
                                        ]}
                                    >
                                        <Input
                                            disabled={selectedServisTipi === "anlasmasiz"}
                                            onBlur={async (e) => {
                                                const tamirhaneKod = e.target.value;
                                                if (tamirhaneKod && selectedServisTipi === "anlasmali") {
                                                    try {
                                                        const response = await fetchTamirhaneBilgisi(token, tamirhaneKod);
                                                        const data = Array.isArray(response) ? response[0] : response;
                                                        if (data) {
                                                            // Önce il kodunu set et ve selected state'i güncelle
                                                            setSelectedServisIl(data.ilKod || undefined);

                                                            // İlçe listesini yükle
                                                            if (data.ilKod) {
                                                                await loadServisIlceListesi(data.ilKod);
                                                            }

                                                            // İlçe listesi yüklendikten SONRA tüm değerleri set et
                                                            form.setFieldsValue({
                                                                servisAdSoyad: data.adSoyadUnvan || "",
                                                                servisIlKodu: data.ilKod || undefined,
                                                                servisIlceKodu: data.ilceKod || undefined,
                                                                servisAdres: data.adres || "",
                                                                servisTelefon: data.telefon || "",
                                                                servisVergiNo: data.vergiNo || "1"
                                                            });

                                                            setIsTamirhaneLoaded(true);
                                                        }
                                                    } catch (err) {
                                                        message.error("Tamirhane bilgisi alınamadı");
                                                        // Tamirhane bilgisi alınamazsa alanları boşalt
                                                        form.setFieldsValue({
                                                            servisAdSoyad: "",
                                                            servisIlKodu: undefined,
                                                            servisIlceKodu: undefined,
                                                            servisAdres: "",
                                                            servisTelefon: "",
                                                            servisVergiNo: ""
                                                        });
                                                        setSelectedServisIl(undefined);
                                                        setIsTamirhaneLoaded(false);
                                                    }
                                                }
                                            }}
                                        />
                                    </Form.Item>
                                </Col>
                                <Col xs={24} md={12}>
                                    <Form.Item label="Tckn/Vergi Numarası"
                                        name="servisVergiNo"
                                        rules={[
                                            { required: true, message: "Bu alan zorunludur" }
                                        ]}>
                                        <Input
                                            maxLength={11}
                                            disabled={selectedServisTipi === "anlasmali" && isTamirhaneLoaded}
                                            onBlur={async (e) => {
                                                const kimlikNo = e.target.value;

                                                // Anlaşmasız servis ve tamirhane yüklü değilse API'yi çağır
                                                if (selectedServisTipi === "anlasmasiz" && !isTamirhaneLoaded && kimlikNo) {
                                                    await handleServisKimlikSorgula(kimlikNo);
                                                }
                                            }}
                                        />
                                    </Form.Item>
                                </Col>
                            </Row>
                            <Row gutter={16}>
                                <Col xs={24} md={12}>
                                    <Form.Item label="Ad/Soyad/Ünvan" name="servisAdSoyad" rules={[{ required: true, message: "Bu alan zorunludur" }]}>
                                        <Input disabled={selectedServisTipi === "anlasmali" && isTamirhaneLoaded} />
                                    </Form.Item>
                                </Col>
                                <Col xs={24} md={12}>
                                    <Form.Item
                                        label="Telefon"
                                        name="servisTelefon"
                                        rules={[
                                            { required: true, message: "Bu alan zorunludur" },
                                            {
                                                validator: (_, value) => {
                                                    if (!value) return Promise.resolve();
                                                    const cleaned = value.replace(/[^\d]/g, '');
                                                    if (cleaned.length === 11 || cleaned.length === 10) {
                                                        return Promise.resolve();
                                                    }
                                                    return Promise.reject(new Error('Telefon 10 veya 11 haneli olmalıdır'));
                                                }
                                            }
                                        ]}
                                    >
                                        <InputMask
                                            mask="0(999) 999 99 99"
                                            maskChar={null}
                                            formatChars={{
                                                '9': '[0-9]'
                                            }}
                                            disabled={selectedServisTipi === "anlasmali" && isTamirhaneLoaded}
                                        >
                                            {(inputProps) => (
                                                <Input
                                                    {...inputProps}
                                                    placeholder="0(___) ___ __ __"
                                                    maxLength={16}
                                                    onKeyPress={(e) => {
                                                        const value = e.currentTarget.value.replace(/[^\d]/g, '');
                                                        if (value.length >= 11) {
                                                            e.preventDefault();
                                                        }
                                                    }}
                                                />
                                            )}
                                        </InputMask>
                                    </Form.Item>
                                </Col>

                            </Row>
                            <Row gutter={16}>
                                <Col xs={24} md={12}>
                                    <Form.Item label="İl" name="servisIlKodu" rules={[{ required: true, message: "Bu alan zorunludur" }]}>
                                        <Select
                                            allowClear
                                            showSearch
                                            optionFilterProp="children"
                                            filterOption={turkishSearch}
                                            disabled={selectedServisTipi === "anlasmali" && isTamirhaneLoaded}
                                            onChange={val => {
                                                setSelectedServisIl(val);
                                                form.setFieldsValue({ servisIlceKodu: undefined });
                                                // İlçeleri yükle
                                                loadServisIlceListesi(val);
                                            }}
                                        >
                                            {ilListesiState.map(il => (
                                                <Option key={il.ilKod || il.ilKodu} value={il.ilKod || il.ilKodu}>
                                                    {il.ilAdi || il.ilAd}
                                                </Option>
                                            ))}
                                        </Select>
                                    </Form.Item>
                                </Col>
                                <Col xs={24} md={12}>
                                    <Form.Item label="İlçe" name="servisIlceKodu" rules={[{ required: true, message: "Bu alan zorunludur" }]}>
                                        <Select
                                            allowClear
                                            showSearch
                                            loading={isServisIlceLoading}
                                            optionFilterProp="children"
                                            filterOption={turkishSearch}
                                            disabled={!selectedServisIl || (selectedServisTipi === "anlasmali" && isTamirhaneLoaded)}
                                        >
                                            {filteredServisIlceler.map(ilce => (
                                                <Option key={ilce.ilceKod || ilce.ilceKodu} value={ilce.ilceKod || ilce.ilceKodu}>
                                                    {ilce.ilceAdi || ilce.ilceAd}
                                                </Option>
                                            ))}
                                        </Select>
                                    </Form.Item>
                                </Col>
                            </Row>
                            <Row gutter={16}>
                                <Col xs={24} md={12}>
                                    <Form.Item label="Açık Adres" name="servisAdres" rules={[{ required: true, message: "Bu alan zorunludur" }]}>
                                        <Input.TextArea
                                            rows={3}
                                            disabled={selectedServisTipi === "anlasmali" && isTamirhaneLoaded}
                                        />
                                    </Form.Item>
                                </Col>

                            </Row>
                        </Panel>
                        <Panel header="Hesap Bilgileri" key="5">
                            <Row gutter={16}>
                                <Col xs={24}>
                                    <Form.Item name="isVekalet" valuePropName="checked">
                                        <Checkbox>Vekalet mi?</Checkbox>
                                    </Form.Item>
                                </Col>
                            </Row>
                            <Row gutter={16}>
                                <Col xs={24} md={12}>
                                    <Form.Item label="Hesap Sahibi Adı" name="hesapSahibiAdi">
                                        <Input placeholder="Hesap sahibinin adını giriniz" />
                                    </Form.Item>
                                </Col>
                                <Col xs={24} md={12}>
                                    <Form.Item label="Hesap Sahibi Soyadı" name="hesapSahibiSoyadi">
                                        <Input placeholder="Hesap sahibinin soyadını giriniz" />
                                    </Form.Item>
                                </Col>
                            </Row>
                            <Row gutter={16}>
                                <Col xs={24} md={12}>
                                    <Form.Item
                                        label="IBAN"
                                        name="hesapSahibiIban"
                                        rules={[
                                            {
                                                validator: (_, value) => {
                                                    if (!value) return Promise.resolve();
                                                    // TR harfleri ve boşlukları temizle
                                                    const cleanedValue = value.replace(/\s/g, '').toUpperCase();
                                                    // IBAN formatı kontrolü (TR + 24 hane)
                                                    if (cleanedValue.startsWith('TR') && cleanedValue.length === 26) {
                                                        return Promise.resolve();
                                                    }
                                                    return Promise.reject(new Error('Geçerli bir TR IBAN giriniz (TR + 24 hane)'));
                                                }
                                            }
                                        ]}
                                    >
                                        <Input
                                            placeholder="TR00 0000 0000 0000 0000 0000 00"
                                            maxLength={34}
                                        />
                                    </Form.Item>
                                </Col>
                            </Row>
                        </Panel>
                    </Collapse>

                    <div className="bildirim-actions">
                        <Button
                            type="primary"
                            htmlType="submit"
                            className="bildirim-btn"
                            loading={isSubmitting}
                        >
                            Kaydet
                        </Button>
                        <Button
                            htmlType="button"
                            className="bildirim-btn"
                            onClick={() => navigate("/kasko")}
                        >
                            Vazgeç
                        </Button>
                    </div>

                    {errorMessage && (
                        <Alert
                            message="Hata"
                            description={errorMessage}
                            type="error"
                            closable
                            onClose={() => setErrorMessage("")}
                            style={{ marginTop: "16px" }}
                            showIcon
                        />
                    )}
                </Form>
            </ConfigProvider>
        </div>
    );
}

export default KaskoIhbarBildirimPage;
